import React, { useState, useEffect, useCallback } from 'react';
import { printHtmlSilently } from '../lib/silentPrint';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { X, Lock, Printer, ClipboardCheck, Users, TrendingDown, TrendingUp, Percent, ChevronDown, ChevronRight, Wallet, Receipt } from 'lucide-react';
import axios from 'axios';
import { CLOSING_METHODS, A_PRAZO as A_PRAZO_ROW, closingLabel, emptyCounted, countedToBreakdown, visibleMethods } from '../lib/closingMethods';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('pt-BR') : '—';
const methodLabelOf = closingLabel;

/**
 * Modal de Conferência de Turno (usado em Resumo de Turnos).
 * Fluxo: gerente vê tudo do turno + digita valores contados por forma de pagamento.
 * Sistema calcula diferença (falta/sobra) e finaliza o fechamento (status=closed).
 */
export const SessionConferenceModal = ({ open, sessionId, onClose, onDone }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [step, setStep] = useState(1); // 1: dados+contagem, 2: resultado
  const [password, setPassword] = useState('');
  const [counted, setCounted] = useState(emptyCounted());
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState({ a_prazo: true, sangrias: true, suprimentos: false, discounts: false, crediario: true, sales: false, other_moves: true });

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/cash-sessions/${sessionId}/details`, { withCredentials: true });
      setDetails(r.data);
      // Pré-preenche counted com breakdown esperado como sugestão? NÃO — conferência cega.
      // Mantém campos vazios para o gerente digitar os valores contados.
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao carregar detalhes do turno');
    } finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => {
    if (open && sessionId) {
      setStep(1); setPassword(''); setNotes(''); setResult(null);
      setCounted(emptyCounted());
      load();
    }
  }, [open, sessionId, load]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { setErr('Digite sua senha de gerente para confirmar.'); return; }
    setSaving(true); setErr('');
    try {
      const counted_breakdown = countedToBreakdown(counted);
      // Endpoint close-by-admin não pede senha do admin, mas exige role admin/manager.
      // Vamos usar reconcile se sessão já estiver closed, ou close-by-admin se pending_close.
      const isPending = details?.status === 'pending_close';
      const endpoint = isPending
        ? `${API}/cash-sessions/${sessionId}/close-by-admin`
        : `${API}/cash-sessions/${sessionId}/reconcile`;
      const resp = await axios.post(endpoint, {
        counted_amount: counted_breakdown.dinheiro,
        counted_breakdown,
        operator_password: password,
        notes: notes || 'Conferência pelo gerente',
      }, { withCredentials: true });
      // Recarrega detalhes atualizados
      const r = await axios.get(`${API}/cash-sessions/${sessionId}/details`, { withCredentials: true });
      setDetails(r.data);
      setResult(resp.data);
      setStep(2);
      if (onDone) onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao conferir turno');
    } finally { setSaving(false); }
  };

  const printReport = () => {
    if (!details) return;
    const html = buildConferenceReceipt(details, counted);
    printHtmlSilently(html);
  };

  const toggle = (k) => setExpanded({ ...expanded, [k]: !expanded[k] });

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[75] flex items-start justify-center p-2 md:p-4 overflow-y-auto" data-testid="session-conference-modal">
      <div className="bg-white rounded-md w-full max-w-4xl my-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-indigo-700 to-purple-700 rounded-t-md sticky top-0 z-10">
          <div className="flex items-center gap-2 text-white">
            <ClipboardCheck size={20} />
            <div>
              <h2 className="text-sm font-black uppercase tracking-wider">Conferência de Turno</h2>
              <p className="text-[10px] opacity-80">Fechamento supervisionado — gerente confere valores</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="conf-close-x"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {loading && <p className="text-sm text-slate-500 text-center py-8">Carregando detalhes...</p>}
          {err && !loading && (
            <Alert variant="destructive"><AlertDescription data-testid="conf-error">{err}</AlertDescription></Alert>
          )}
          {details && !loading && (
            <>
              {/* Cabeçalho do turno */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 border border-slate-200 rounded p-3 text-xs">
                <Info label="Operador" value={details.user_name} />
                <Info label="Terminal" value={details.terminal || '—'} />
                <Info label="Aberto" value={fmtDate(details.opened_at)} />
                <Info label="Encerrado" value={fmtDate(details.shift_ended_at || details.closed_at)} />
                <Info label="Fundo inicial" value={money(details.initial_amount)} strong />
                <Info label="Nº vendas" value={String(details.sales_count || 0)} strong />
                <Info label="Vendas do turno (sem fundo)" value={money(details.total_sales)} strong />
                <Info label="Status" value={details.status === 'pending_close' ? '🟡 Aguardando' : details.status === 'closed' ? '✅ Conferido' : details.status} />
              </div>

              {/* Vendas A Prazo */}
              <CollapsibleSection
                open={expanded.a_prazo}
                onToggle={() => toggle('a_prazo')}
                title="Vendas A Prazo (Crediário)"
                icon={<Users size={14} />}
                count={details.a_prazo_count}
                total={details.a_prazo_total}
                accent="bg-yellow-50 border-yellow-300"
                testId="conf-a-prazo"
              >
                {details.a_prazo_sales.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Nenhuma venda a prazo neste turno.</p>
                ) : (
                  <table className="w-full text-xs" data-testid="conf-a-prazo-table">
                    <thead className="text-left text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="pb-1">#</th>
                        <th className="pb-1">Cliente</th>
                        <th className="pb-1">Horário</th>
                        <th className="pb-1 text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.a_prazo_sales.map((s) => (
                        <tr key={s.id} className="border-t border-yellow-200">
                          <td className="py-1">#{s.sale_number || '—'}</td>
                          <td className="py-1 font-medium">{s.customer_name}</td>
                          <td className="py-1 text-slate-500">{fmtDate(s.created_at)}</td>
                          <td className="py-1 text-right font-mono font-bold text-yellow-800">{money(s.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CollapsibleSection>

              {/* Sangrias */}
              <CollapsibleSection
                open={expanded.sangrias}
                onToggle={() => toggle('sangrias')}
                title="Sangrias (Retiradas)"
                icon={<TrendingDown size={14} />}
                count={details.sangrias_count}
                total={details.sangrias_total}
                accent="bg-red-50 border-red-300"
                testId="conf-sangrias"
              >
                {details.sangrias.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Nenhuma sangria neste turno.</p>
                ) : (
                  <table className="w-full text-xs" data-testid="conf-sangrias-table">
                    <thead className="text-left text-[10px] uppercase text-slate-500">
                      <tr><th className="pb-1">Motivo</th><th className="pb-1">Horário</th><th className="pb-1 text-right">Valor</th></tr>
                    </thead>
                    <tbody>
                      {details.sangrias.map((s) => (
                        <tr key={s.id} className="border-t border-red-200">
                          <td className="py-1">{s.description}</td>
                          <td className="py-1 text-slate-500">{fmtDate(s.created_at)}</td>
                          <td className="py-1 text-right font-mono font-bold text-red-700">− {money(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CollapsibleSection>

              {/* Suprimentos */}
              <CollapsibleSection
                open={expanded.suprimentos}
                onToggle={() => toggle('suprimentos')}
                title="Suprimentos (Aportes)"
                icon={<TrendingUp size={14} />}
                count={details.suprimentos_count}
                total={details.suprimentos_total}
                accent="bg-emerald-50 border-emerald-300"
                testId="conf-suprimentos"
              >
                {details.suprimentos.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Nenhum suprimento neste turno.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="text-left text-[10px] uppercase text-slate-500">
                      <tr><th className="pb-1">Motivo</th><th className="pb-1">Horário</th><th className="pb-1 text-right">Valor</th></tr>
                    </thead>
                    <tbody>
                      {details.suprimentos.map((s) => (
                        <tr key={s.id} className="border-t border-emerald-200">
                          <td className="py-1">{s.description}</td>
                          <td className="py-1 text-slate-500">{fmtDate(s.created_at)}</td>
                          <td className="py-1 text-right font-mono font-bold text-emerald-700">+ {money(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CollapsibleSection>

              {/* Descontos */}
              <CollapsibleSection
                open={expanded.discounts}
                onToggle={() => toggle('discounts')}
                title="Descontos Concedidos"
                icon={<Percent size={14} />}
                count={details.discounts_count}
                total={details.discounts_total}
                accent="bg-amber-50 border-amber-300"
                testId="conf-discounts"
              >
                {details.discounts.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Nenhum desconto concedido neste turno.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="text-left text-[10px] uppercase text-slate-500">
                      <tr><th className="pb-1">#</th><th className="pb-1">Motivo</th><th className="pb-1 text-right">Desconto</th><th className="pb-1 text-right">Venda</th></tr>
                    </thead>
                    <tbody>
                      {details.discounts.map((d) => (
                        <tr key={d.id} className="border-t border-amber-200">
                          <td className="py-1">#{d.sale_number || '—'}</td>
                          <td className="py-1">{d.reason}</td>
                          <td className="py-1 text-right font-mono font-bold text-amber-700">− {money(d.amount)}</td>
                          <td className="py-1 text-right font-mono text-slate-600">{money(d.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CollapsibleSection>

              {/* Recebimentos de Crediário no Turno */}
              <CollapsibleSection
                open={expanded.crediario}
                onToggle={() => toggle('crediario')}
                title="Recebimentos de Crediário no Turno"
                icon={<Wallet size={14} />}
                count={details.crediario_count || 0}
                total={details.crediario_total || 0}
                accent="bg-teal-50 border-teal-300"
                testId="conf-crediario"
              >
                {(!details.crediario_receipts || details.crediario_receipts.length === 0) ? (
                  <p className="text-xs text-slate-500 italic">Nenhum recebimento de crediário neste turno.</p>
                ) : (
                  <>
                    <table className="w-full text-xs" data-testid="conf-crediario-table">
                      <thead className="text-left text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="pb-1">Cliente</th>
                          <th className="pb-1">Horário</th>
                          <th className="pb-1">Forma</th>
                          <th className="pb-1">Operador</th>
                          <th className="pb-1 text-right">Valor</th>
                          <th className="pb-1 text-right">Saldo restante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.crediario_receipts.map((r) => (
                          <tr key={r.id} className="border-t border-teal-200">
                            <td className="py-1 font-medium">{r.customer_name}{r.sale_number ? ` (#${r.sale_number})` : ''}</td>
                            <td className="py-1 text-slate-500">{fmtDate(r.created_at)}</td>
                            <td className="py-1">{methodLabelOf(r.payment_method)}</td>
                            <td className="py-1 text-slate-600">{r.operator}</td>
                            <td className="py-1 text-right font-mono font-bold text-teal-700">{money(r.amount)}</td>
                            <td className="py-1 text-right font-mono">
                              {r.fully_paid ? <span className="text-emerald-700 font-bold">Quitada</span>
                                : (r.outstanding_after !== null && r.outstanding_after !== undefined ? money(r.outstanding_after) : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-2 border-t border-teal-300 pt-2">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Totais de crediário recebido por forma</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1" data-testid="conf-crediario-by-method">
                        {visibleMethods(details.crediario_by_method).map((m) => (
                          <div key={m.key} className="flex justify-between bg-white/70 border border-teal-200 rounded px-2 py-1">
                            <span className="text-xs">{m.icon} {m.label}</span>
                            <span className="font-mono font-bold text-xs text-teal-800" data-testid={`conf-crediario-${m.key}`}>
                              {money((details.crediario_by_method || {})[m.key] || 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">Estes valores já estão incluídos no esperado de cada forma acima.</p>
                    </div>
                  </>
                )}
              </CollapsibleSection>

              {/* Auditoria: vendas do turno por forma de pagamento */}
              <CollapsibleSection
                open={expanded.sales}
                onToggle={() => toggle('sales')}
                title="Vendas do Turno (auditoria por forma de pagamento)"
                icon={<Receipt size={14} />}
                count={details.sales_count || 0}
                total={details.total_sales || 0}
                accent="bg-slate-50 border-slate-300"
                testId="conf-sales-audit"
              >
                <SalesAudit sales={details.sales_list || []} />
              </CollapsibleSection>

              {/* Outras movimentações em dinheiro (devoluções, despesas pagas do caixa, etc.) */}
              {(details.other_cash_moves || []).length > 0 && (
                <CollapsibleSection
                  open={expanded.other_moves}
                  onToggle={() => toggle('other_moves')}
                  title="Outras Movimentações em Dinheiro no Caixa"
                  icon={<Wallet size={14} />}
                  count={details.other_cash_moves.length}
                  total={details.other_cash_moves.reduce((s, m) => s + (m.type === 'entrada' ? 1 : -1) * Number(m.amount || 0), 0)}
                  accent="bg-orange-50 border-orange-300"
                  testId="conf-other-moves"
                >
                  <table className="w-full text-xs" data-testid="conf-other-moves-table">
                    <thead className="text-left text-[10px] uppercase text-slate-500">
                      <tr><th className="pb-1">Descrição</th><th className="pb-1">Horário</th><th className="pb-1 text-right">Valor</th></tr>
                    </thead>
                    <tbody>
                      {details.other_cash_moves.map((m) => (
                        <tr key={m.id} className="border-t border-orange-200">
                          <td className="py-1">{m.description}</td>
                          <td className="py-1 text-slate-500">{fmtDate(m.created_at)}</td>
                          <td className={`py-1 text-right font-mono font-bold ${m.type === 'entrada' ? 'text-emerald-700' : 'text-red-700'}`}>
                            {m.type === 'entrada' ? '+ ' : '− '}{money(m.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CollapsibleSection>
              )}

              {/* STEP 1: formulário de contagem */}
              {step === 1 && (
                <form onSubmit={submit} className="border-2 border-indigo-300 bg-indigo-50 rounded p-4 space-y-3" data-testid="conf-form">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock size={16} className="text-indigo-700" />
                    <p className="text-sm font-bold uppercase tracking-wider text-indigo-900">Valores Contados (Conferência)</p>
                  </div>
                  <p className="text-xs text-indigo-800">Digite o valor REAL contado em cada forma de pagamento. O sistema não mostra os valores esperados aqui — isso é conferência cega.</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {visibleMethods(details.breakdown_expected, counted).map((m) => (
                      <div key={m.key}>
                        <Label className="text-[10px] uppercase font-bold text-slate-700">{m.icon} {m.label}</Label>
                        <Input
                          type="number" step="0.01" min="0" inputMode="decimal" placeholder="0,00"
                          value={counted[m.key]}
                          onChange={(e) => setCounted({ ...counted, [m.key]: e.target.value })}
                          className="mt-0.5 text-sm font-bold"
                          data-testid={`conf-counted-${m.key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase font-bold text-slate-700">Observações (opcional)</Label>
                    <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: FALTA R$ 5 — anotado com operador" data-testid="conf-notes" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase font-bold text-slate-700"><Lock size={10} className="inline mr-1" />Senha do gerente</Label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="conf-password" />
                  </div>
                  {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="conf-cancel-btn">Cancelar</Button>
                    <Button type="submit" className="bg-indigo-700 hover:bg-indigo-800 text-white font-bold" disabled={saving || !password} data-testid="conf-submit-btn">
                      {saving ? 'Conferindo...' : 'Confirmar Conferência'}
                    </Button>
                  </div>
                </form>
              )}

              {/* STEP 2: resultado */}
              {step === 2 && details && (
                <ResultBox details={details} counted={counted} onPrint={printReport} onDone={onClose} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SalesAudit = ({ sales }) => {
  const [method, setMethod] = useState('all');
  const methods = [...CLOSING_METHODS, A_PRAZO_ROW];
  const totals = methods.reduce((acc, m) => {
    acc[m.key] = sales.filter(s => s.method === m.key).reduce((s, x) => s + Number(x.amount || 0), 0);
    return acc;
  }, {});
  const rows = method === 'all' ? sales : sales.filter(s => s.method === method);
  if (sales.length === 0) return <p className="text-xs text-slate-500 italic">Nenhuma venda neste turno.</p>;
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-2" data-testid="conf-sales-audit-filters">
        <button type="button" onClick={() => setMethod('all')}
          className={`text-[11px] px-2 py-0.5 rounded-full border ${method === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-300 text-slate-700'}`}
          data-testid="conf-sales-filter-all">Todas ({sales.length})</button>
        {methods.filter(m => totals[m.key] > 0.005).map(m => (
          <button key={m.key} type="button" onClick={() => setMethod(m.key)}
            className={`text-[11px] px-2 py-0.5 rounded-full border ${method === m.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-300 text-slate-700'}`}
            data-testid={`conf-sales-filter-${m.key}`}>
            {m.icon} {m.label} · {money(totals[m.key])}
          </button>
        ))}
      </div>
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-xs" data-testid="conf-sales-audit-table">
          <thead className="text-left text-[10px] uppercase text-slate-500 sticky top-0 bg-slate-50">
            <tr><th className="pb-1">#</th><th className="pb-1">Horário</th><th className="pb-1">Cliente</th><th className="pb-1">Forma</th><th className="pb-1 text-right">Valor</th></tr>
          </thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={`${s.id}-${s.method}-${i}`} className="border-t border-slate-200">
                <td className="py-1 font-bold text-blue-700">#{s.sale_number || '—'}{s.is_mixed && <span className="ml-1 text-[9px] text-slate-500 font-normal">(mista)</span>}</td>
                <td className="py-1 text-slate-500">{fmtDate(s.created_at)}</td>
                <td className="py-1 text-slate-700">{s.customer_name || '—'}</td>
                <td className="py-1">{s.method === 'a_prazo' ? A_PRAZO_ROW.label : methodLabelOf(s.method)}</td>
                <td className="py-1 text-right font-mono font-bold">{money(s.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-500 mt-1">Use esta lista para localizar vendas lançadas na forma de pagamento errada (ex.: passou no débito mas foi registrado como voucher).</p>
    </div>
  );
};

const Info = ({ label, value, strong }) => (
  <div>
    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
    <p className={`text-slate-900 ${strong ? 'font-bold' : ''}`}>{value}</p>
  </div>
);

const CollapsibleSection = ({ open, onToggle, title, icon, count, total, accent, children, testId }) => (
  <div className={`border-2 rounded ${accent}`} data-testid={testId}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/40 transition-colors"
    >
      <div className="flex items-center gap-2">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {icon}
        <span className="font-bold text-sm text-slate-900">{title}</span>
        <span className="text-[10px] font-bold bg-white/70 text-slate-700 px-2 py-0.5 rounded-full">{count}</span>
      </div>
      <span className="font-mono font-black text-sm text-slate-900" data-testid={`${testId}-total`}>{money(total)}</span>
    </button>
    {open && <div className="px-3 pb-3 pt-1">{children}</div>}
  </div>
);

const ResultBox = ({ details, counted, onPrint, onDone }) => {
  const bd = details.breakdown_expected || {};
  const bdDiff = details.breakdown_difference || {};
  // difference = TOTAL (soma das diferenças de TODAS as formas); cash_difference = só dinheiro
  const rows = visibleMethods(bd, counted, details.counted_breakdown);
  const totalDiff = details.difference !== undefined && details.difference !== null
    ? Number(details.difference)
    : rows.reduce((s, m) => s + (Number(bdDiff[m.key]) || 0), 0);
  const cashDiff = Number(details.cash_difference !== undefined && details.cash_difference !== null ? details.cash_difference : (bdDiff.dinheiro || 0));
  const totalAbsDiff = rows.reduce((s, m) => s + Math.abs(Number(bdDiff[m.key]) || 0), 0);
  const overallOK = Math.abs(totalDiff) <= 0.005;
  const allMethodsOK = totalAbsDiff <= 0.005;
  const isFaltaCash = cashDiff < -0.005;
  const isSobraCash = cashDiff > 0.005;
  const initial = Number(details.initial_amount || 0);
  const cashSales = Number(details.total_cash_sales || 0);
  const totalIn = Number(details.total_in || 0);
  const totalOut = Number(details.total_out || 0);
  const aPrazoTotal = Number(details.a_prazo_total || 0);

  return (
    <div className="space-y-3">
      <div className={`rounded p-4 border-2 ${overallOK ? 'bg-emerald-50 border-emerald-400' : totalDiff > 0 ? 'bg-amber-50 border-amber-400' : 'bg-red-50 border-red-400'}`} data-testid="conf-result-overall">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-600">Status Geral do Fechamento — soma de todas as formas</p>
        <p className={`text-2xl font-black mt-1 ${overallOK ? 'text-emerald-700' : totalDiff > 0 ? 'text-amber-700' : 'text-red-700'}`} data-testid="conf-total-diff">
          {overallOK
            ? (allMethodsOK ? '✓ CAIXA BATEU EM TODAS AS FORMAS' : '✓ CAIXA BATEU NO TOTAL (formas compensaram)')
            : `${totalDiff > 0 ? 'SOBRA' : 'FALTA'} TOTAL: ${totalDiff > 0 ? '+' : '−'}${money(Math.abs(totalDiff))}`}
        </p>
        {!allMethodsOK && (
          <p className="text-xs text-slate-600 mt-1" data-testid="conf-partial-note">
            {overallOK
              ? `Há diferenças por forma (${money(totalAbsDiff)} no total absoluto) que se anulam — ex.: falta numa forma e sobra em outra.`
              : 'Soma das diferenças de dinheiro, débito, crédito, PIX e demais formas listadas abaixo.'}
          </p>
        )}
      </div>

      <div className="border border-slate-200 rounded overflow-hidden">
        <div className="bg-slate-100 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-600">
          Esperado × Contado por Forma de Pagamento
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-slate-500">
            <tr><th className="text-left px-3 py-1">Forma</th><th className="text-right px-3">Esperado</th><th className="text-right px-3">Contado</th><th className="text-right px-3">Diferença</th></tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const exp = Number(bd[m.key] || 0);
              const cnt = Number(counted?.[m.key] || 0);
              const d = Number(bdDiff[m.key] || 0);
              return (
                <tr key={m.key} className="border-t border-slate-100" data-testid={`conf-row-${m.key}`}>
                  <td className="px-3 py-1 text-xs">{m.icon} {m.label}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{money(exp)}</td>
                  <td className="px-3 py-1 text-right font-mono font-bold text-xs">{money(cnt)}</td>
                  <td className={`px-3 py-1 text-right font-mono font-bold text-xs ${Math.abs(d) < 0.005 ? 'text-emerald-700' : d < 0 ? 'text-red-700' : 'text-amber-700'}`}>
                    {Math.abs(d) < 0.005 ? 'OK' : `${d > 0 ? '+' : ''}${d.toFixed(2)}`}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold" data-testid="conf-row-total">
              <td className="px-3 py-1 text-xs">Σ TOTAL (todas as formas)</td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(rows.reduce((s, m) => s + (Number(bd[m.key]) || 0), 0))}</td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(rows.reduce((s, m) => s + (Number(counted?.[m.key]) || 0), 0))}</td>
              <td className={`px-3 py-1 text-right font-mono text-xs ${overallOK ? 'text-emerald-700' : totalDiff < 0 ? 'text-red-700' : 'text-amber-700'}`} data-testid="conf-row-total-diff">
                {overallOK ? 'OK' : `${totalDiff > 0 ? '+' : ''}${totalDiff.toFixed(2)}`}
              </td>
            </tr>
            {/* Linha A Prazo — abaixo de Cheque. Não é dinheiro físico (não conta na diferença). */}
            <tr className="border-t border-yellow-300 bg-yellow-50" data-testid="conf-row-a-prazo">
              <td className="px-3 py-1 text-xs">
                {A_PRAZO_ROW.icon} <strong>{A_PRAZO_ROW.label}</strong>
                <span className="text-[9px] text-yellow-800 ml-1">(não é dinheiro na mão — conta a receber)</span>
              </td>
              <td className="px-3 py-1 text-right font-mono font-bold text-xs text-yellow-800" data-testid="conf-row-a-prazo-expected">{money(aPrazoTotal)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs text-slate-400">—</td>
              <td className="px-3 py-1 text-right font-mono text-xs text-slate-400">—</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={`rounded p-3 border-2 ${isFaltaCash ? 'bg-red-50 border-red-300' : isSobraCash ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
        <p className="text-xs uppercase tracking-wider font-bold text-slate-600">Saldo em Dinheiro no Caixa</p>
        <div className="mt-1 mb-2 p-2 rounded bg-white/70 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Cálculo do esperado:</p>
          <p className="font-mono text-xs text-slate-700 mt-0.5" data-testid="cash-expected-formula">
            Fundo <strong>{money(initial)}</strong>
            {' + '}Vendas Dinheiro <strong>{money(cashSales)}</strong>
            {totalIn > 0.005 && <> {' + '}Entradas <strong>{money(totalIn)}</strong></>}
            {totalOut > 0.005 && <> {' − '}Saídas/Sangrias <strong>{money(totalOut)}</strong></>}
            {' = '}<strong className="text-emerald-700">{money(details.expected_amount)}</strong>
          </p>
        </div>
        <div className="flex justify-between text-sm"><span>Esperado</span><span className="font-mono">{money(details.expected_amount)}</span></div>
        <div className="flex justify-between text-sm"><span>Contado</span><span className="font-mono font-bold">{money(details.counted_amount)}</span></div>
        <div className="border-t border-slate-300 pt-1 mt-1">
          <p className={`text-lg font-black ${isFaltaCash ? 'text-red-700' : isSobraCash ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="conf-cash-diff">
            DIFERENÇA SÓ DINHEIRO: {money(Math.abs(cashDiff))} {isFaltaCash ? '(FALTA)' : isSobraCash ? '(SOBRA)' : '(OK)'}
          </p>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onPrint} data-testid="conf-print-btn"><Printer size={14} className="mr-2" />Imprimir 80mm</Button>
        <Button onClick={onDone} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="conf-done-btn">Concluir</Button>
      </div>
    </div>
  );
};

const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

const buildConferenceReceipt = (d, counted) => {
  const bd = d.breakdown_expected || {};
  const bdDiff = d.breakdown_difference || {};
  const cb = d.counted_breakdown || counted || {};
  const totalDiff = d.difference !== undefined && d.difference !== null ? Number(d.difference) : Object.values(bdDiff).reduce((s, v) => s + (Number(v) || 0), 0);
  const cashDiff = Number(d.cash_difference !== undefined && d.cash_difference !== null ? d.cash_difference : (bdDiff.dinheiro || 0));
  const diffLabel = cashDiff < -0.005 ? 'FALTA' : cashDiff > 0.005 ? 'SOBRA' : 'OK';
  const totalAbsDiff = Object.values(bdDiff).reduce((s, v) => s + Math.abs(Number(v) || 0), 0);
  const overallOK = Math.abs(totalDiff) <= 0.005;
  const totalLabel = overallOK ? (totalAbsDiff <= 0.005 ? 'BATEU EM TUDO' : 'BATEU NO TOTAL') : (totalDiff > 0 ? 'SOBRA TOTAL' : 'FALTA TOTAL');
  const rowLine = (label, value, extra = '') => `<div class="row"><span>${escapeHtml(label)}</span><span class="right">${escapeHtml(value)}${extra ? ' ' + escapeHtml(extra) : ''}</span></div>`;
  const bdRows = visibleMethods(bd, cb).map(m => {
    const exp = bd[m.key] || 0;
    const cnt = Number(cb[m.key] || 0);
    const dd = bdDiff[m.key];
    const extra = dd !== undefined && Math.abs(dd) > 0.005 ? `(${dd > 0 ? '+' : ''}${dd.toFixed(2)})` : '';
    return rowLine(m.label, `${money(cnt)} / esp ${money(exp)}`, extra);
  }).join('');
  const apList = d.a_prazo_sales.length === 0 ? '<div>(nenhuma)</div>' : d.a_prazo_sales.map(s => rowLine(`#${s.sale_number} ${s.customer_name}`, money(s.total))).join('');
  const sangriaList = d.sangrias.length === 0 ? '<div>(nenhuma)</div>' : d.sangrias.map(s => rowLine(s.description, `- ${money(s.amount)}`)).join('');
  const credReceipts = d.crediario_receipts || [];
  const credByMethod = d.crediario_by_method || {};
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
  const credList = credReceipts.length === 0 ? '<div>(nenhum)</div>' : credReceipts.map(r => {
    const saldo = r.fully_paid ? 'QUITADA' : (r.outstanding_after !== null && r.outstanding_after !== undefined ? `resta ${money(r.outstanding_after)}` : '');
    const head = rowLine(`${fmtTime(r.created_at)} ${r.customer_name}`, money(r.amount));
    const sub = `<div style="font-size:9px;color:#000">${escapeHtml(methodLabelOf(r.payment_method))} · ${escapeHtml(r.operator || '-')}${saldo ? ' · ' + escapeHtml(saldo) : ''}</div>`;
    return head + sub;
  }).join('');
  const credMethodRows = visibleMethods(credByMethod).map(m => rowLine(m.label, money(credByMethod[m.key] || 0))).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Conferência Turno</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; color: #000; }
  .center { text-align: center; } .right { text-align: right; }
  .bold { font-weight: 700; } .row { display: flex; justify-content: space-between; gap: 4px; }
  hr { border: none; border-top: 1px dashed #000; margin: 3px 0; }
  h2 { font-size: 12px; margin: 2px 0; text-transform: uppercase; }
  .big { font-size: 13px; font-weight: 700; }
</style></head><body>
  <div class="center bold">================================</div>
  <div class="center bold">   CONFERÊNCIA DE TURNO</div>
  <div class="center bold">================================</div>
  <div>Operador: <span class="bold">${escapeHtml(d.user_name || '-')}</span></div>
  <div>Abertura: ${escapeHtml(fmtDate(d.opened_at))}</div>
  <div>Encerrado: ${escapeHtml(fmtDate(d.shift_ended_at || d.closed_at))}</div>
  <hr/><h2>Vendas</h2>
  ${rowLine('Nº vendas', String(d.sales_count || 0))}
  ${rowLine('Total vendido', money(d.total_sales))}
  <hr/><h2>Vendas A Prazo (${d.a_prazo_count})</h2>
  ${apList}
  ${rowLine('Total a prazo', money(d.a_prazo_total))}
  <hr/><h2>Sangrias (${d.sangrias_count})</h2>
  ${sangriaList}
  ${rowLine('Total sangrias', money(d.sangrias_total))}
  <hr/><h2>Recebimentos Crediário (${d.crediario_count || 0})</h2>
  ${credList}
  ${rowLine('Total recebido crediário', money(d.crediario_total || 0))}
  <div style="font-size:9px">-- por forma --</div>
  ${credMethodRows}
  <hr/><h2>Formas de Pagamento</h2>
  ${bdRows}
  <hr/><h2>Dinheiro no Caixa</h2>
  ${rowLine('Esperado', money(d.expected_amount))}
  ${rowLine('Contado', money(d.counted_amount))}
  <div class="row big"><span>DIF. SO DINHEIRO</span><span>${money(Math.abs(cashDiff))} ${diffLabel}</span></div>
  <hr/><h2>Status Geral (todas as formas)</h2>
  <div class="row big"><span>${totalLabel}</span><span>${overallOK ? 'OK' : (totalDiff > 0 ? '+' : '-') + money(Math.abs(totalDiff))}</span></div>
  <hr/><br/>
  <div>Assinatura gerente:</div><br/>
  <div class="center">_______________________________</div><br/>
  <div>Assinatura operador:</div><br/>
  <div class="center">_______________________________</div>
  <br/><div class="center bold">================================</div>
  <div class="center">Smart Flow POS</div>
</body></html>`;
};

export default SessionConferenceModal;
